import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Mail, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function UnsubscribePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUnsubscribe = async () => {
    if (!token) {
      setError("Neplatný odkaz pro odhlášení");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: apiError } = await supabase.functions.invoke('unsubscribe-email', {
        body: { token }
      });

      if (apiError) {
        throw new Error(apiError.message);
      }

      if (!data.success) {
        throw new Error(data.error || "Nepodařilo se odhlásit z odběru");
      }

      setSuccess(true);
      toast({
        title: "Úspěch",
        description: "Úspěšně jste se odhlásili z emailových oznámení"
      });

    } catch (err: any) {
      console.error('Unsubscribe error:', err);
      setError(err.message || "Došlo k chybě při odhlašování");
      toast({
        variant: "destructive",
        title: "Chyba",
        description: err.message || "Nepodařilo se odhlásit z odběru"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Odhlášení z odběru</CardTitle>
          <CardDescription>
            Emailové oznámení z Dostihy CZ
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!success && !error && !loading && (
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Chcete se odhlásit z emailových oznámení? Po odhlášení již nebudete dostávat newsletters a další emaily od nás.
              </p>
              <Button 
                onClick={handleUnsubscribe}
                disabled={loading}
                className="w-full"
                variant="destructive"
              >
                {loading ? "Odhlašování..." : "Odhlásit se z odběru"}
              </Button>
            </div>
          )}

          {loading && (
            <div className="text-center space-y-4">
              <div className="mx-auto w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-muted-foreground">Zpracovávám vaši žádost...</p>
            </div>
          )}

          {success && (
            <div className="text-center space-y-4">
              <CheckCircle className="mx-auto w-16 h-16 text-green-500" />
              <div>
                <h3 className="text-lg font-semibold text-green-600">Úspěšně odhlášeno!</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Již nebudete dostávat emailová oznámení z Dostihy CZ. Pokud si to rozmyslíte, můžete se znovu přihlásit v nastavení vašeho účtu.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="text-center space-y-4">
              <XCircle className="mx-auto w-16 h-16 text-red-500" />
              <div>
                <h3 className="text-lg font-semibold text-red-600">Chyba při odhlašování</h3>
                <p className="text-sm text-muted-foreground mt-2">{error}</p>
              </div>
              <Button 
                onClick={handleUnsubscribe}
                disabled={loading}
                className="w-full"
                variant="outline"
              >
                Zkusit znovu
              </Button>
            </div>
          )}

          <div className="pt-4 border-t">
            <Button 
              onClick={() => navigate('/')}
              variant="ghost"
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zpět na hlavní stránku
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}